import React, { useEffect, useState } from 'react';
import { PauseIcon, PlayIcon } from 'lucide-react';
import { stills } from '../data/projects';
import { EMBED_ERROR, toDrivePreviewUrl } from '../utils/drive';
interface VideoStageProps {
  driveUrl: string;
  label: string;
  runtime: string;
}
function toClock(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
}
export function VideoStage({
  driveUrl,
  label,
  runtime
}: VideoStageProps) {
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const total = 1724;
  const embedUrl = toDrivePreviewUrl(driveUrl);
  const linkProvided = Boolean(driveUrl.trim());
  /** Only the link itself can be judged — Drive is cross-origin and opaque. */
  const showError = linkProvided && !embedUrl;
  useEffect(() => {
    setPlaying(false);
    setPosition(0);
  }, [driveUrl]);
  useEffect(() => {
    if (!playing || embedUrl) return;
    const timer = window.setInterval(() => {
      setPosition(current => current >= total ? 0 : current + 1);
    }, 120);
    return () => window.clearInterval(timer);
  }, [playing, embedUrl, total]);
  return <figure className="m-0" data-id="element-460" data-mp="31l9prdjpobdj">
      <div className="relative aspect-video w-full overflow-hidden bg-ink" data-id="element-461" data-mp="2xkhekera3wem">
        {embedUrl ? <iframe title={label} src={embedUrl} allow="autoplay; fullscreen" allowFullScreen className="h-full w-full border-0" data-id="element-462" data-mp="29uwlv45bpks5" /> : showError ? <div className="flex h-full w-full flex-col items-start justify-end p-6 sm:p-8" data-id="element-463" data-mp="2inglo7xcpvek">
            <img src={stills.studio} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20 grayscale" data-id="element-464" data-mp="2dvyjxhni7qsj" />
            <div className="absolute inset-0 bg-ink/70" data-id="element-465" data-mp="1m5amogoyb5k5" />
            <div className="relative" data-id="element-466" data-mp="1m4srzohxszec">
              <p className="label m-0 text-paper/50" data-id="element-467" data-mp="3la2bip6uyget">Preview Unavailable</p>
              <p className="mt-4 max-w-md font-display text-xl leading-snug text-paper sm:text-2xl" data-id="element-468" data-mp="3l7zmo8hfytbo">
                {EMBED_ERROR}
              </p>
              <a href={driveUrl} target="_blank" rel="noreferrer" className="label mt-7 inline-block border border-paper/50 px-5 py-3 text-paper transition-colors duration-150 ease-out hover:bg-paper hover:text-ink" data-id="element-469" data-mp="3uzltztqns895">
                Open in Google Drive
              </a>
            </div>
          </div> : <>
            <img src={stills.studio} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40 grayscale" data-id="element-470" data-mp="3c8cjbh55jdfz" />
            <div className="absolute inset-0 bg-ink/40" data-id="element-471" data-mp="rjoi9gcums8p" />
            <div className="absolute inset-0 flex flex-col justify-between p-6 sm:p-8" data-id="element-472" data-mp="rl9ct5z5yb6k">
              <p className="label text-paper/60" data-id="element-473" data-mp="wgewrz909fs0">Google Drive Video Preview</p>
              <div className="flex items-end justify-between gap-6" data-id="element-474" data-mp="2gyz6phnddh0g">
                <div data-id="element-475" data-mp="2c0nx980zl8ir">
                  <p className="label text-paper/50" data-id="element-476" data-mp="2c28tr36v31j9">{playing ? 'Playing' : 'Paused'}</p>
                  <p className="mt-2 font-sans text-xs tracking-wide2 text-paper/80" data-id="element-477" data-mp="279pdfygshsar">
                    {toClock(position)} / {runtime}
                  </p>
                </div>
                <button type="button" onClick={() => setPlaying(current => !current)} aria-label={playing ? 'Pause preview' : 'Play preview'} className="flex h-16 w-16 items-center justify-center rounded-full border border-paper/40 text-paper transition-colors duration-150 ease-out hover:border-paper hover:bg-paper hover:text-ink sm:h-20 sm:w-20" data-id="element-478" data-mp="1xc1lthehd0wt">
                  {playing ? <PauseIcon className="h-5 w-5" strokeWidth={1} data-id="element-479" data-mp="3ina1kmb04ii5" /> : <PlayIcon className="h-5 w-5 translate-x-[1px]" strokeWidth={1} data-id="element-480" data-mp="3dogjegkrs3bn" />}
                </button>
              </div>
            </div>
            <div className="absolute bottom-0 left-0 h-px w-full bg-paper/20" data-id="element-481" data-mp="2fdfeuof1g6z5">
              <div className="h-px bg-paper transition-[width] duration-150 ease-linear" style={{
            width: `${position / total * 100}%`
          }} data-id="element-482" data-mp="21fqqv7pocjpv" />
            </div>
          </>}
      </div>

      <figcaption className="mt-3 flex flex-wrap items-center justify-between gap-3" data-id="element-483" data-mp="1rnusrwnl8tln">
        <span className="label text-mute" data-id="element-484" data-mp="1whfil1f12h9r">{label}</span>
        {embedUrl ? <a href={driveUrl} target="_blank" rel="noreferrer" className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-485" data-mp="1ipiud8ghcrin">
            Open in Google Drive
          </a> : <span className="label text-mute" data-id="element-486" data-mp="1dpd704h8tr6d">
            {showError ? 'Check the Drive link' : 'Awaiting Drive preview link'}
          </span>}
      </figcaption>
    </figure>;
}